import { useMe } from "@/api/services/admin/auth";
import { useProductList } from "@/api/services/admin/product";
import { useProductContractsByOrganization } from "@/api/services/admin/product-contract";
import { useEnum } from "@/api/services/global/enum";
import { Button } from "@/components/elements/Button";
import { CheckboxGroup } from "@/components/elements/CheckboxGroup";
import { EllipsisText } from "@/components/elements/EllipsisText";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { Table } from "@/components/elements/Table";
import { ELoginPurpose } from "@/constants/enum";
import { TProductLoginForm, productLoginSchema } from "@/features/session/schema/productLoginSchema";
import { TProductLoginState } from "@/features/session/types/productLoginState";
import { useTable } from "@/hooks/useTable";
import { AppRoutes } from "@/routes/config";
import { background, button, text } from "@/theme/colors";
import { EContractStatus } from "@/types/enum";
import { yupResolver } from "@hookform/resolvers/yup";
import LoginRoundedIcon from "@mui/icons-material/LoginRounded";
import { Box, Radio, Stack, SxProps, Theme, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { FC, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";

type TProductRow = {
  id: string;
  name?: string;
  lastLoginAt?: string | null;
  referenceProductId?: number;
  contractStatus?: string;
};

type TProductLoginCardProps = {
  organizationId: string;
  selectedProductId: string;
  onSelectProduct: (id: string) => void;
  sx?: SxProps<Theme>;
};

export const ProductLoginCard: FC<TProductLoginCardProps> = ({ organizationId, selectedProductId, onSelectProduct, sx }) => {
  const { data: meResult } = useMe();

  const loginPurposeNote = meResult?.data?.loginPurposeNote ?? "";

  const methods = useForm<TProductLoginForm>({
    defaultValues: { purposes: [], otherText: "" },
    resolver: yupResolver(productLoginSchema),
    mode: "all",
  });
  const { control, watch, setValue, getValues } = methods;
  const navigate = useNavigate();

  const selectedPurposes = watch("purposes");
  const isOtherSelected = selectedPurposes.includes(ELoginPurpose.OTHER);

  // prefill otherText with the saved note only while "その他" is selected
  useEffect(() => {
    if (isOtherSelected) {
      if (!getValues("otherText")) {
        setValue("otherText", loginPurposeNote);
      }
    } else {
      setValue("otherText", "");
    }
  }, [isOtherSelected, loginPurposeNote, setValue, getValues]);

  const hasOrg = Boolean(organizationId);
  const canLogin = hasOrg && Boolean(selectedProductId) && selectedPurposes.length > 0;

  const { data: contractData, isFetching: isLoadingProductContract } = useProductContractsByOrganization(organizationId, {
    includeCenter: true,
    sorts: ["impersonationCreatedAt|DESC"],
  });
  const { data: productData } = useProductList();
  const { data: loginPurposes } = useEnum("ELoginPurpose");

  useEffect(() => {
    if (contractData) {
      // Default-select the product the admin last impersonated into, but skip it if that row is not selectable (no linked reference product).
      const lastImpersonated = contractData?.data?.find((contract) => contract.isLastImpersonated && Boolean(contract.referenceProductId));
      if (lastImpersonated) {
        onSelectProduct(String(lastImpersonated.id));
      }
    }
  }, [contractData]);

  const rows: TProductRow[] = hasOrg
    ? (contractData?.data ?? []).map((contract) => ({
        id: String(contract.id),
        name: contract.product?.name,
        lastLoginAt: contract.lastLoginAt,
        referenceProductId: contract.referenceProductId ?? undefined,
        contractStatus: contract.status,
      }))
    : [
        // { id: "center", name: "irohana center" },
        ...(productData?.data ?? []).map((product) => ({
          id: String(product.id),
          name: product.name,
          lastLoginAt: product.productContract?.lastLoginAt,
        })),
      ];

  // A product row is not selectable unless it is "center" or already has a linked reference product.
  const isRowDisabled = (row: TProductRow) =>
    row.id !== "center" && (!row.referenceProductId || row.contractStatus === EContractStatus.DELETED_CONTRACT);

  const columns = useMemo<MRT_ColumnDef<TProductRow>[]>(
    () => [
      {
        id: "select",
        header: "",
        size: 54,
        // keep the radio column fixed at 48px (requires layoutMode: "grid")
        grow: false,
        Cell: ({ row }) => {
          const productId = row.original.id;
          return hasOrg ? (
            <Radio
              size="small"
              checked={hasOrg && productId === selectedProductId}
              disabled={!hasOrg}
              onChange={() => onSelectProduct(productId)}
              sx={{
                color: button.primary,
                "&.Mui-checked": { color: button.primary },
              }}
            />
          ) : (
            <Box
              sx={{
                width: 16,
                height: 16,
                backgroundColor: background.white,
                borderRadius: "100%",
              }}
            />
          );
        },
      },
      {
        accessorKey: "name",
        header: "プロダクト名",
        size: 120,
        Cell: ({ row }) => (
          <EllipsisText title={row.original.name ?? ""} variant="body14" color={hasOrg ? text.primary : text.disable} maxWidth={104} />
        ),
      },
    ],
    [hasOrg, selectedProductId, onSelectProduct],
  );

  const { table } = useTable({
    columns,
    rows,
    layoutMode: "grid",
    disableRow: isRowDisabled,
    onClickRow: hasOrg ? (row) => onSelectProduct(row.id) : undefined,
    muiTableBodyRowProps: hasOrg
      ? undefined
      : { sx: { pointerEvents: "none", backgroundColor: `${background.disable} !important`, color: `${text.disable} !important` } },
  });

  const onSubmit = (formData: TProductLoginForm) => {
    const selectedContract = contractData?.data?.find((contract) => String(contract.id) === selectedProductId);
    const productCode = selectedContract?.product?.code ?? "";

    const state: TProductLoginState = {
      organizationId,
      productId: selectedProductId,
      productCode,
      purposes: formData.purposes,
      otherText: formData.otherText,
    };
    navigate(AppRoutes.userSelect, { state });
  };

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={productLoginSchema} enableBlocker={false}>
      <InnerBox
        padding="32px"
        sx={sx}
        title={
          <Typography variant="head20Bold" color={text.primary} width="100%" textAlign="center">
            プロダクトログイン
          </Typography>
        }
      >
        <Table table={table} maxHeight="45vh" isLoading={isLoadingProductContract} />

        <Stack gap={1.5} pt={3}>
          <FormField control={control} name="purposes">
            <CheckboxGroup
              disabled={!organizationId}
              options={loginPurposes}
              sx={{
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            />
          </FormField>
          <FormField control={control} name="otherText" noLabel>
            <Input placeholder="その他を選択した場合に入力ください" disabled={!isOtherSelected} />
          </FormField>
          <Typography variant="body14" color={text.secondary}>
            目的は記録されるため、適切に選択してください。
          </Typography>
        </Stack>

        <Stack gap={2} pt={4}>
          <Button type="submit" variant="primary" fullWidth disabled={!canLogin} startIcon={<LoginRoundedIcon sx={{ fontSize: 18 }} />}>
            {hasOrg ? "ログインするユーザー選択へ" : "顧客画面へログイン"}
          </Button>
          <Button variant="secondary" fullWidth disabled={!hasOrg} component={Link} to={AppRoutes.organizationDetail.replace(":id", organizationId)}>
            組織管理
          </Button>
          <Button variant="ghost" fullWidth component={Link} to={AppRoutes.home}>
            管理画面へ戻る
          </Button>
        </Stack>
      </InnerBox>
    </Form>
  );
};
